// regionMoveFlow — 範囲の移動・コピーの画面の「純粋な部分」(docs/_wip/region-move-2026-10-15.md「UI」)。
// React / DOM / engine に触れない。範囲の正規化、レイヤー選択の文言、置き先の既定、
// はみ出し部分の矩形、送る items の組み立てなど。hook (`useRegionMove`) と画面はこれを呼ぶだけ。

import { CLASSIC_LAYER_COUNT } from '@oekaki/protocol';

export interface IntRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface CanvasSize {
  readonly w: number;
  readonly h: number;
}

/** 表示ラベル: 内部 id → 「レイヤーN」(N = id + 1。`classicLayerLabel` と同じ作成順ネーミング)。 */
export const regionLayerLabel = (id: number): string => `レイヤー${id + 1}`;

/** 全レイヤー id (昇順)。 */
export const allLayerIds = (count: number = CLASSIC_LAYER_COUNT): number[] =>
  Array.from({ length: count }, (_, i) => i);

/** 範囲の外接矩形 (2 点、順不同) をキャンバス内の整数矩形にする。幅か高さが 0 なら null。 */
export const normalizeSelection = (a: Point, b: Point, canvas: CanvasSize): IntRect | null => {
  const clamp = (v: number, max: number): number => Math.max(0, Math.min(Math.round(v), max));
  const x0 = clamp(Math.min(a.x, b.x), canvas.w);
  const y0 = clamp(Math.min(a.y, b.y), canvas.h);
  const x1 = clamp(Math.max(a.x, b.x), canvas.w);
  const y1 = clamp(Math.max(a.y, b.y), canvas.h);
  if (x1 - x0 < 1 || y1 - y0 < 1) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
};

/** 「動かすレイヤー」の閉じた表示: `レイヤー2` / `レイヤー2・3` / `全部のレイヤー`。0 枚は `レイヤーなし`。 */
export const summarizeSourceLayers = (
  ids: readonly number[],
  count: number = CLASSIC_LAYER_COUNT,
): string => {
  const sorted = [...new Set(ids)].sort((p, q) => p - q);
  if (sorted.length === 0) return 'レイヤーなし';
  if (sorted.length >= count && count > 1) return '全部のレイヤー';
  const first = sorted[0] as number;
  if (sorted.length === 1) return regionLayerLabel(first);
  return `レイヤー${sorted.map((id) => id + 1).join('・')}`;
};

/** チェックを 1 つ付け外しした新しい配列 (昇順)。 */
export const toggleSourceLayer = (ids: readonly number[], id: number): number[] => {
  const set = new Set(ids);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return [...set].sort((p, q) => p - q);
};

/** 「全部選ぶ」: 全部チェック済みなら変えない (トグルにしない)。 */
export const selectAllSourceLayers = (count: number = CLASSIC_LAYER_COUNT): number[] =>
  allLayerIds(count);

/** [移動][コピー] を押せるか: チェックが 1 枚以上 + 範囲がある。 */
export const canRunRegionMove = (srcLayerIds: readonly number[], hasSelection: boolean): boolean =>
  hasSelection && srcLayerIds.length > 0;

/**
 * 置き先: 1 枚から取ったときだけ選べる。選んでいなければ「取った元」が既定。
 * 2 枚以上から取ったとき (それぞれの層の中で動かす) は null = 置き先は出さない。
 */
export const effectiveDstLayerId = (
  srcLayerIds: readonly number[],
  override: number | null,
): number | null => {
  if (srcLayerIds.length !== 1) return null;
  return override ?? (srcLayerIds[0] as number);
};

/** sendRegionMove に渡す items。複数のときは各 item で src === dst (server が検証)。 */
export const buildRegionItems = (
  srcLayerIds: readonly number[],
  dstLayerId: number | null,
): Array<{ srcLayerId: number; dstLayerId: number }> =>
  [...srcLayerIds]
    .sort((p, q) => p - q)
    .map((src) => ({
      srcLayerId: src,
      dstLayerId: srcLayerIds.length === 1 && dstLayerId !== null ? dstLayerId : src,
    }));

/** 置き先の矩形 (浮かせている絵)。 */
export const floatRect = (src: IntRect, dst: Point): IntRect => ({
  x: dst.x,
  y: dst.y,
  w: src.w,
  h: src.h,
});

/**
 * 浮かせている絵を動かしたあとの置き先。全部が画面外に出て絵が消えてしまわないよう、
 * キャンバスと最低 1px は重なるように寄せる (はみ出し自体は許す。確定すると切れる)。
 */
export const clampFloatDst = (
  dst: Point,
  size: { w: number; h: number },
  canvas: CanvasSize,
): Point => {
  const clamp = (v: number, lo: number, hi: number): number =>
    Math.max(lo, Math.min(Math.round(v), hi));
  return {
    x: clamp(dst.x, 1 - size.w, canvas.w - 1),
    y: clamp(dst.y, 1 - size.h, canvas.h - 1),
  };
};

/** 浮かせている絵のうち、キャンバスの外にはみ出す部分 (確定すると切れる)。最大 4 つの矩形。 */
export const outsideStrips = (rect: IntRect, canvas: CanvasSize): IntRect[] => {
  const strips: IntRect[] = [];
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;
  const ix0 = Math.max(left, 0);
  const ix1 = Math.min(right, canvas.w);
  const iy0 = Math.max(top, 0);
  const iy1 = Math.min(bottom, canvas.h);
  // 重なりが無いときは全体がはみ出し。
  if (ix1 <= ix0 || iy1 <= iy0) return [{ ...rect }];
  if (iy0 > top) strips.push({ x: left, y: top, w: rect.w, h: iy0 - top });
  if (bottom > iy1) strips.push({ x: left, y: iy1, w: rect.w, h: bottom - iy1 });
  if (ix0 > left) strips.push({ x: left, y: iy0, w: ix0 - left, h: iy1 - iy0 });
  if (right > ix1) strips.push({ x: ix1, y: iy0, w: right - ix1, h: iy1 - iy0 });
  return strips;
};

/** パネルの行に添える小さな印。 */
export interface RegionLayerRoles {
  /** 置き先のレイヤー (1 枚から取ったときだけ。複数のときは null)。 */
  readonly dstLayerId: number | null;
  /** 取った元のレイヤー。 */
  readonly srcLayerIds: readonly number[];
}

/** あるレイヤー行に添える文言: 置き先と取った元が同じ層なら「置き先・取った元」。 */
export const regionRoleTag = (roles: RegionLayerRoles | null, layerId: number): string | null => {
  if (roles === null) return null;
  const isDst = roles.dstLayerId === layerId;
  const isSrc = roles.srcLayerIds.includes(layerId);
  if (isDst && isSrc) return '置き先・取った元';
  if (isDst) return '置き先';
  if (isSrc) return '取った元';
  return null;
};
